/* Offline sale queue + background sync.
 *
 * Why this exists: the shop's internet drops often. A sale rung up during an
 * outage must not be lost — it is stamped with a UUID, saved on the phone, and
 * pushed to the VPS automatically when the connection returns. The backend
 * dedupes on that UUID (sales/api_views.py), so re-sending a sale can never
 * create a duplicate, even if the phone dies mid-sync and retries later.
 *
 * Storage: a single localStorage key holding a JSON array. localStorage is
 * synchronous (no open races), survives app restarts, and these payloads are a
 * few hundred bytes each — a whole day of offline sales is well under the quota.
 *
 * This module is UI-agnostic: it exposes createSaleResilient() for the sale
 * pages and a subscribe() stream ({ online, pending, syncing }) for the
 * offline indicator. It owns no React.
 */
import { apiClient } from '../services/authService';

const STORAGE_KEY = 'kistore_offline_sales';
const SYNC_INTERVAL_MS = 60 * 1000;

// ---- tiny pub/sub so the UI can reflect queue + connectivity state ----
const listeners = new Set();
let syncing = false;

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122-ish fallback for older WebViews without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (err) {
    // Quota / private-mode failure. Nothing we can safely do but log — the
    // caller already has the sale in memory for this session.
    console.error('Failed to persist offline sale queue:', err);
  }
  emit();
}

export function pendingCount() {
  return readQueue().filter((s) => s.status !== 'error').length;
}

function errorCount() {
  return readQueue().filter((s) => s.status === 'error').length;
}

function snapshot() {
  return {
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    pending: pendingCount(),
    errors: errorCount(),
    syncing,
  };
}

function emit() {
  const snap = snapshot();
  listeners.forEach((fn) => {
    try { fn(snap); } catch { /* a bad listener must not break sync */ }
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

// ---- queue mutations ----
function enqueue(payload) {
  const queue = readQueue();
  queue.push({
    uuid: payload.client_uuid,
    payload,
    createdAt: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    lastError: null,
  });
  writeQueue(queue);
}

function removeByUuid(id) {
  writeQueue(readQueue().filter((s) => s.uuid !== id));
}

function markError(id, message) {
  const queue = readQueue();
  const rec = queue.find((s) => s.uuid === id);
  if (rec) {
    rec.status = 'error';
    rec.lastError = message;
    rec.attempts = (rec.attempts || 0) + 1;
  }
  writeQueue(queue);
}

// A thrown error with no `.response` means the request never reached the server
// (offline, DNS failure, timeout) — the sale should be queued/retried. A thrown
// error WITH a response means the server saw it and rejected it (validation,
// auth) — retrying unchanged is pointless.
function isNetworkError(err) {
  return !err || !err.response;
}

/**
 * Create a sale that tolerates being offline.
 * Returns { id, offline, client_uuid, ...serverData }.
 *  - online success: real server sale (has a numeric id).
 *  - offline: queued locally, returns { offline: true, id: null, client_uuid }.
 *  - server rejection (4xx): throws, so the page shows the real error.
 */
export async function createSaleResilient(payload) {
  const body = {
    ...payload,
    client_uuid: payload.client_uuid || uuid(),
    // Capture WHEN the sale happened, so an outage-then-sync keeps the real time.
    date_added: payload.date_added || new Date().toISOString(),
  };

  try {
    const res = await apiClient.post('sales/', body);
    // Network is clearly up — flush anything queued from a previous outage.
    if (pendingCount() > 0) syncNow();
    return { ...res.data, offline: false, client_uuid: body.client_uuid };
  } catch (err) {
    if (isNetworkError(err)) {
      enqueue(body);
      return { offline: true, id: null, client_uuid: body.client_uuid };
    }
    throw err.response?.data || err;
  }
}

/**
 * Try to push every queued sale to the server. Safe to call anytime; it no-ops
 * if already syncing or offline. Stops on the first network failure (still
 * offline) and leaves the rest queued; server-rejected sales are parked as
 * 'error' so one bad record can't block the whole queue forever.
 */
export async function syncNow() {
  if (syncing) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const due = readQueue().filter((s) => s.status !== 'error');
  if (due.length === 0) return;

  syncing = true;
  emit();
  let syncedAny = false;
  try {
    for (const rec of due) {
      try {
        // Backend returns 200 (already stored) or 201 (created); both are success.
        await apiClient.post('sales/', rec.payload);
        removeByUuid(rec.uuid);
        syncedAny = true;
      } catch (err) {
        if (isNetworkError(err)) break; // connection dropped again — retry later
        // Server reached and rejected this specific sale. Park it, keep going.
        markError(rec.uuid, err.response?.data?.error || `HTTP ${err.response?.status}`);
      }
    }
  } finally {
    syncing = false;
    emit();
  }
  return syncedAny;
}

// Sales parked as 'error' (server rejected them) — surfaced for manual review.
export function getFailedSales() {
  return readQueue().filter((s) => s.status === 'error');
}

export function discardFailedSale(id) {
  removeByUuid(id);
}

let started = false;
export function startSaleSync() {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => { emit(); syncNow(); });
  window.addEventListener('offline', emit);
  // Retry loop for the case where 'online' fires but the server is still
  // unreachable, or the app was reopened offline with a backlog.
  setInterval(() => { if (navigator.onLine) syncNow(); }, SYNC_INTERVAL_MS);
  // Flush any backlog left from a previous session on startup.
  syncNow();
}
